/**
 * Handles return from Stripe 3D Secure redirect.
 *
 * Reads `payment_intent`, `redirect_status`, and `order_id` from the URL,
 * dispatches order finalization on success, posts a confirm-payment call to
 * the backend, and surfaces a redirect failure message back to the page.
 *
 * Cleans up Stripe params from the URL via `history.replaceState` (no nav).
 *
 * Guarded so it only runs once per page mount even if dependencies change.
 */

import { useEffect, useRef, useState } from 'react'
import { useAppDispatch } from '@/store/hooks'
import { clearCart } from '@/store/cartSlice'
import { setEstimatedTime, setOrderStatus } from '@/store/orderSlice'
import { menuWidgetApi } from '@/store/convenienceAccessor'
import { getEnvironmentConfig } from '@/config/environment'

interface UseStripeRedirectHandlerArgs {
  /** Persisted order id from Redux (preferred when available). */
  orderId: string | null | undefined
}

interface UseStripeRedirectHandlerResult {
  /** Set to a non-null user-facing message when the 3DS redirect failed. */
  stripeRedirectError: string | null
}

export function useStripeRedirectHandler({
  orderId,
}: UseStripeRedirectHandlerArgs): UseStripeRedirectHandlerResult {
  const dispatch = useAppDispatch()
  const config = getEnvironmentConfig()
  const [addConfirmPayment] = menuWidgetApi.confirmPayment.add()
  const [stripeRedirectError, setStripeRedirectError] = useState<string | null>(null)

  // Guard to ensure the 3DS redirect confirm-payment call only runs once
  const redirectConfirmRan = useRef(false)

  useEffect(() => {
    if (redirectConfirmRan.current) return
    const params = new URLSearchParams(window.location.search)
    const paymentIntent = params.get('payment_intent')
    const redirectStatus = params.get('redirect_status')

    if (!paymentIntent) return

    if (redirectStatus === 'succeeded') {
      redirectConfirmRan.current = true

      // Payment succeeded after 3D Secure redirect — finalize the order
      dispatch(clearCart())
      dispatch(setOrderStatus('confirmed'))
      const eta = new Date(Date.now() + 30 * 60 * 1000).toISOString()
      dispatch(setEstimatedTime(eta))

      // Notify the backend that payment succeeded (same confirm-payment call
      // used in the non-3DS path in useCheckoutFlow.ts).
      const persistedOrderId = orderId
      const urlOrderId = params.get('order_id')
      const effectiveOrderId = persistedOrderId || urlOrderId

      if (effectiveOrderId) {
        addConfirmPayment({
          id: effectiveOrderId,
          restaurant_id: config.restaurantId,
        })
          .unwrap()
          .catch((err: unknown) => {
            // Log but don't block UI — payment already succeeded on Stripe's side.
            // The webhook will eventually reconcile if this call fails.
            console.warn('[TRACKING] confirm-payment after 3DS redirect failed:', err)
          })
      } else {
        console.warn('[TRACKING] No orderId available after 3DS redirect — relying on webhook')
      }

      // Clean up Stripe params from the URL without triggering a navigation
      const cleanUrl = window.location.pathname
      window.history.replaceState({}, '', cleanUrl)
    } else if (redirectStatus === 'failed') {
      redirectConfirmRan.current = true
      // Payment failed after redirect — show error and allow retry
      setStripeRedirectError(
        'Your payment was not completed. Please go back to checkout and try again.'
      )
      // Clean up params from the URL
      const cleanUrl = window.location.pathname
      window.history.replaceState({}, '', cleanUrl)
    } else if (redirectStatus === 'processing') {
      // Payment is still in-flight — mark as handled so the effect doesn't
      // re-fire on dep changes and issue a spurious confirm-payment call.
      redirectConfirmRan.current = true
    }
  }, [dispatch, orderId, addConfirmPayment, config.restaurantId])

  return { stripeRedirectError }
}
